import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import useAuthStore from '../store/authStore';
import useTenantStore from '../store/tenantStore';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';
import LessonAssignments from '../components/LessonAssignments';
import LessonDiscussions from '../components/LessonDiscussions';
import useOnlineStatus from '../utils/useOnlineStatus';
import { saveLessonOffline, getOfflineLesson, isLessonAvailableOffline, deleteOfflineLesson } from '../utils/offlineLessons';
import ProtectedVideoPlayer from '../components/ProtectedVideoPlayer';

function LessonDetail() {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewMode = new URLSearchParams(location.search).get('preview') === '1';
  const { isAuthenticated, user } = useAuthStore();
  const isFeatureEnabled = useTenantStore((state) => state.isFeatureEnabled);
  const isTeacher = user?.roles?.some((role) => role.name?.toLowerCase() === 'teacher');
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();
  const isNative = Capacitor.isNativePlatform();
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewingOfflineCopy, setViewingOfflineCopy] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [progress, setProgress] = useState(null);
  const [nextLesson, setNextLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedQuizHistory, setSelectedQuizHistory] = useState(null);
  const [quizAttempts, setQuizAttempts] = useState({});

  // Simple markdown-like text formatter
  const formatContent = (content) => {
    if (!content) return '';

    // Replace **text** with bold
    let formatted = content.replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-gray-900">$1</strong>');

    // Replace *text* with italic
    formatted = formatted.replace(/\*(.+?)\*/g, '<em class="italic">$1</em>');

    // Split by lines and process each
    const lines = formatted.split('\n');
    let html = '';
    let inList = false;

    for (let line of lines) {
      line = line.trim();

      if (!line) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += '<br/>';
        continue;
      }

      // Check for list items
      if (line.startsWith('- ')) {
        if (!inList) {
          html += '<ul class="list-disc list-inside mb-4 space-y-2 ml-4">';
          inList = true;
        }
        html += `<li class="ml-4">${line.substring(2)}</li>`;
      } else {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<p class="mb-3 leading-relaxed">${line}</p>`;
      }
    }

    if (inList) {
      html += '</ul>';
    }

    return html;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setProgress(null);
    setNextLesson(null);
    fetchLessonDetails();
    if (isFeatureEnabled('resources')) fetchAttachments();
    if (isFeatureEnabled('quizzes')) fetchQuizzes();
    if (!isPreviewMode) initializeLessonProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, isAuthenticated, navigate, isPreviewMode]);

  const fetchLessonDetails = async () => {
    if (isNative && !isOnline) {
      const cached = await getOfflineLesson(lessonId);
      if (cached) {
        setLesson(cached.lesson);
        setAttachments(cached.attachments.map((a) => ({ ...a, file: a.dataUrl })));
        setViewingOfflineCopy(true);
        setLoading(false);
        return;
      }
      addNotification(t('This lesson was not downloaded for offline use.'), 'error');
      navigate(`/modules/${moduleId}`);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/content/lessons/${lessonId}/`);
      setLesson(response.data);
      setViewingOfflineCopy(false);
    } catch (error) {
      const message = error.response?.status === 403
        ? (error.response.data?.detail || (isPreviewMode ? t('Enroll to unlock this lesson.') : t('Complete the previous lesson to unlock this one.')))
        : t('Failed to load lesson details');
      addNotification(message, 'error');
      navigate(`/modules/${moduleId}${isPreviewMode ? '?preview=1' : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttachments = async () => {
    if (isNative && !isOnline) return; // already populated from the offline cache above
    try {
      const response = await api.get(`/content/attachments/?lesson=${lessonId}`);
      const data = response.data.results || response.data || [];
      setAttachments(Array.isArray(data) ? data : []);
    } catch (error) {
      setAttachments([]);
    }
  };

  useEffect(() => {
    if (!isNative) return;
    isLessonAvailableOffline(lessonId).then(setIsDownloaded);
  }, [lessonId, isNative]);

  const handleDownloadForOffline = async () => {
    setDownloading(true);
    try {
      await saveLessonOffline(lesson, attachments, moduleId);
      setIsDownloaded(true);
      addNotification(t('Lesson saved for offline use'), 'success');
    } catch (err) {
      addNotification(t('Failed to save lesson for offline use'), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleRemoveDownload = async () => {
    await deleteOfflineLesson(lessonId);
    setIsDownloaded(false);
    addNotification(t('Download removed'), 'success');
  };

  const fetchQuizzes = async () => {
    if (isNative && !isOnline) return;
    try {
      const response = await api.get(`/quizzes/quizzes/?lesson=${lessonId}`);
      const quizzesData = response.data.results || response.data || [];
      setQuizzes(Array.isArray(quizzesData) ? quizzesData : []);

      // Fetch attempts for each quiz
      if (!isPreviewMode && quizzesData.length > 0) {
        await fetchQuizAttempts(quizzesData);
      }
    } catch (error) {
      setQuizzes([]);
    }
  };

  const fetchQuizAttempts = async (quizzesData) => {
    try {
      const response = await api.get('/quizzes/attempts/', { params: { lesson: lessonId, page_size: 100 } });
      const allAttempts = response.data.results || response.data || [];

      // Group attempts by quiz
      const attemptsByQuiz = {};
      quizzesData.forEach(quiz => {
        attemptsByQuiz[quiz.id] = allAttempts
          .filter(attempt => attempt.quiz === quiz.id)
          .sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
      });

      setQuizAttempts(attemptsByQuiz);
    } catch (error) {
      // non-critical; leave attempts empty
    }
  };

  const initializeLessonProgress = async () => {
    if (isPreviewMode || (isNative && !isOnline)) return;
    try {
      const response = await api.get('/content/lesson-progress/', { params: { lesson: lessonId, student: user.id } });
      const progressData = response.data.results || response.data || [];
      const lessonProgress = Array.isArray(progressData)
        ? progressData.find(p => p.lesson === parseInt(lessonId, 10) && p.student === user.id)
        : null;

      if (lessonProgress) {
        setProgress(lessonProgress);
        return;
      }

      const created = await api.post('/content/lesson-progress/', {
        lesson: parseInt(lessonId, 10),
        is_completed: false,
        completion_percentage: 0
      });
      setProgress(created.data);
    } catch (error) {
      // non-critical
    }
  };

  const fetchNextLesson = async () => {
    if (isNative && !isOnline) return;
    try {
      const response = await api.get(`/content/lessons/${lessonId}/next/`);
      if (response.data?.next_lesson) {
        setNextLesson(response.data.next_lesson);
        return;
      }
    } catch (error) {
      // Fall back to the ordered module payload below. This also supports a
      // frontend being updated while an older API instance is still rolling.
    }

    try {
      const response = await api.get(`/academics/modules/${moduleId}/`);
      const moduleData = response.data || response;
      const orderedLessons = (moduleData.categories || []).flatMap(
        (category) => category.lessons || []
      );
      const currentIndex = orderedLessons.findIndex(
        (item) => String(item.id) === String(lessonId)
      );
      const candidate = currentIndex >= 0 ? orderedLessons[currentIndex + 1] : null;
      setNextLesson(candidate && !candidate.is_locked ? candidate : null);
    } catch (error) {
      setNextLesson(null);
    }
  };

  useEffect(() => {
    if (progress?.is_completed) fetchNextLesson();
    else setNextLesson(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress?.is_completed, lessonId]);

  const handleCompleteLesson = async () => {
    try {
      let response;
      if (progress) {
        response = await api.patch(`/content/lesson-progress/${progress.id}/`, {
          is_completed: true,
          completion_percentage: 100,
          completed_at: new Date().toISOString()
        });
      } else {
        response = await api.post('/content/lesson-progress/', {
          lesson: parseInt(lessonId),
          is_completed: true,
          completion_percentage: 100,
          completed_at: new Date().toISOString()
        });
      }
      setProgress(response.data || response);
      addNotification(t('Lesson marked as complete!'), 'success');
    } catch (error) {
      addNotification(t('Failed to mark lesson as complete'), 'error');
    }
  };

  const openQuizHistory = (quiz) => {
    setSelectedQuizHistory({
      quiz,
      attempts: quizAttempts[quiz.id] || []
    });
    setShowHistoryModal(true);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
    return videoIdMatch ? videoIdMatch[1] : null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  if (loading || !lesson) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopNav title={t('Loading...')} />
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  const embedUrl = getYouTubeEmbedUrl(lesson.video_url);
  const youtubeVideoId = getYouTubeVideoId(lesson.video_url);
  const isCompleted = progress?.is_completed || false;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav
          title={lesson.category_name ? t('{{category}} | {{title}}', { category: lesson.category_name, title: lesson.title }) : lesson.title}
          subtitle={lesson.duration_minutes ? t('{{count}} minutes', { count: lesson.duration_minutes }) : ''}
        />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {viewingOfflineCopy && (
              <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-2.5 flex items-center gap-2">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.828-2.828m2.828 2.828L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.83m-4.243 2.83a9 9 0 01-2.532-7.244m2.532 7.244L3 21m6.464-15.536L3 3" />
                </svg>
                {t("You're offline — showing your downloaded copy of this lesson.")}
              </div>
            )}
            {isPreviewMode && (
              <div className="mb-4 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <span className="font-semibold">{t('Preview mode')}:</span>{' '}
                {t('Your progress and quiz attempts will not be saved.')}
              </div>
            )}

            {/* Header with Back Button */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <button
                onClick={() => navigate(`/modules/${moduleId}${isPreviewMode ? '?preview=1' : ''}`)}
                className="flex items-center text-gray-600 hover:text-primary-600 transition"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('Back to Module')}
              </button>

              <div className="flex items-center gap-2">
                {isNative && !viewingOfflineCopy && !isPreviewMode && (
                  isDownloaded ? (
                    <button
                      onClick={handleRemoveDownload}
                      className="flex items-center gap-1.5 px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
                    >
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {t('Downloaded')}
                    </button>
                  ) : (
                    <button
                      onClick={handleDownloadForOffline}
                      disabled={downloading}
                      className="flex items-center gap-1.5 px-4 py-2 text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition text-sm font-medium disabled:opacity-60"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                      </svg>
                      {downloading ? t('Saving...') : t('Download for Offline')}
                    </button>
                  )
                )}

                {!isPreviewMode && lesson.course_progress && (
                  <div
                    className="flex items-center gap-1.5"
                    title={t('{{completed}} of {{total}} lessons completed', lesson.course_progress)}
                  >
                    <div className="h-1.5 w-12 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full bg-primary-600"
                        style={{ width: `${lesson.course_progress.percent}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-500 tabular-nums">
                      {lesson.course_progress.percent}%
                    </span>
                  </div>
                )}

                {!isPreviewMode && !isCompleted && (
                  <button
                    onClick={handleCompleteLesson}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                  >
                    {t('Mark as Complete')}
                  </button>
                )}

                {!isPreviewMode && isCompleted && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center text-green-600 font-medium">
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {t('Completed')}
                    </div>
                    {nextLesson && (
                      <button
                        type="button"
                        onClick={() => navigate(`/modules/${moduleId}/lessons/${nextLesson.id}`)}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700"
                      >
                        {t('Next lesson')}
                        <svg className="h-4 w-4 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Column - Video and Content */}
              <div className="flex-1 space-y-6 lg:w-3/4">
                {/* Video Player */}
                {(embedUrl || lesson.video_stream_url) && (
                  <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="aspect-video">
                      {embedUrl && isNative ? (
                        // YouTube rejects <iframe> embeds from a native app's
                        // WebView origin (capacitor://localhost / https://localhost)
                        // with error 153 — it only works from a real https domain,
                        // which is what the plain website has. So on native we open
                        // it in the system browser (Chrome Custom Tabs / SFSafariViewController)
                        // instead of embedding it.
                        <button
                          type="button"
                          onClick={() => Browser.open({ url: lesson.video_url })}
                          className="relative w-full h-full group"
                        >
                          {youtubeVideoId && (
                            <img
                              src={`https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`}
                              alt={lesson.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 flex flex-col items-center justify-center gap-2 transition-colors">
                            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                              <svg className="w-7 h-7 text-red-600 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                            </div>
                            <span className="text-white text-sm font-medium">{t('Watch on YouTube')}</span>
                          </div>
                        </button>
                      ) : embedUrl ? (
                        // YouTube/Vimeo embeds are outside our control — none
                        // of the protections below (signed URLs, HLS chunking,
                        // watermark) can apply to a third party's own player.
                        <iframe
                          src={embedUrl}
                          title={lesson.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : lesson.mobile_only && !isNative ? (
                        // The teacher restricted this video to the mobile app —
                        // browsers have no way to block or detect screen
                        // recording (unlike Android's FLAG_SECURE / iOS capture
                        // detection in the native app), so the website shows
                        // this message instead of playing the video at all.
                        <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gray-900 text-center p-6">
                          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <p className="text-white text-sm sm:text-base font-medium max-w-sm">
                            {t('This video is only available in the mobile app.')}
                          </p>
                        </div>
                      ) : (
                        <ProtectedVideoPlayer
                          src={lesson.hls_playlist_url || lesson.video_stream_url}
                          isHls={!!lesson.hls_playlist_url}
                          fallbackSrc={lesson.video_stream_url}
                          title={lesson.title}
                          onExpired={fetchLessonDetails}
                          className="w-full h-full"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Lesson Content */}
                <div className="bg-white rounded-xl shadow-md p-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('Lesson Content')}</h2>
                  <div
                    className="prose prose-lg max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: formatContent(lesson.content) }}
                  />
                </div>
              </div>

              {/* Right Column - Attachments and Quizzes */}
              <div className="space-y-6 lg:w-1/4">
                {/* Attachments */}
                {isFeatureEnabled('resources') && attachments.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {t('Attachments')}
                    </h2>
                    <div className="space-y-3">
                      {attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="p-3 border border-gray-200 rounded-lg hover:shadow-md transition"
                        >
                          <div className="flex items-start mb-2">
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                              <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-800 text-sm truncate">{attachment.title}</h3>
                              {attachment.description && (
                                <p className="text-xs text-gray-500 line-clamp-2">{attachment.description}</p>
                              )}
                              {attachment.file_size && (
                                <p className="text-xs text-gray-400 mt-1">
                                  {(attachment.file_size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              )}
                            </div>
                          </div>
                          <a
                            href={attachment.file}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs font-medium text-center"
                          >
                            {t('Download')}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quizzes */}
                {isFeatureEnabled('quizzes') && quizzes.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('Quizzes')}
                    </h2>
                    <div className="space-y-3">
                      {quizzes.map((quiz) => {
                        const attempts = quizAttempts[quiz.id] || [];
                        const hasAttempts = attempts.length > 0;
                        const bestScore = hasAttempts ? Math.max(...attempts.map(a => a.score)) : 0;

                        return (
                          <div
                            key={quiz.id}
                            className="p-3 border border-gray-200 rounded-lg hover:shadow-md transition"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h3 className="font-medium text-gray-800 text-sm">{quiz.title}</h3>
                                {hasAttempts && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {t('Best: {{score}}% • {{count}} attempts', { score: bestScore.toFixed(1), count: attempts.length })}
                                  </p>
                                )}
                              </div>
                              {hasAttempts && (
                                <button
                                  onClick={() => openQuizHistory(quiz)}
                                  className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition"
                                  title={t('View History')}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            <button
                              onClick={() => navigate(`/quizzes/${quiz.id}${isPreviewMode ? `?preview=1&module=${moduleId}` : ''}`)}
                              className="block w-full px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-xs font-medium"
                            >
                              {isPreviewMode ? t('Check my score') : (hasAttempts ? t('Retake Quiz') : t('Start Quiz'))}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!isPreviewMode && isFeatureEnabled('assignments') && <LessonAssignments lessonId={lessonId} isTeacher={isTeacher} />}

                {/* Empty State */}
                {(isFeatureEnabled('resources') || isFeatureEnabled('quizzes')) && attachments.length === 0 && quizzes.length === 0 && (
                  <div className="bg-white rounded-xl shadow-md p-6 text-center">
                    <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-800 mb-1">{t('No Materials')}</h3>
                    <p className="text-xs text-gray-600">{t('No attachments or quizzes available.')}</p>
                  </div>
                )}
              </div>
            </div>

            {!isPreviewMode && isFeatureEnabled('discussions') && <div className="mt-6">
              <LessonDiscussions lessonId={lessonId} isTeacher={isTeacher} />
            </div>}
          </div>
        </main>
      </div>

      {/* Quiz History Modal */}
      {showHistoryModal && selectedQuizHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-purple-600 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{selectedQuizHistory.quiz.title}</h2>
                  <p className="text-purple-100 text-sm mt-1">
                    {t('{{count}} attempts', { count: selectedQuizHistory.attempts.length })}
                  </p>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="p-2 hover:bg-purple-700 rounded-lg transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              {selectedQuizHistory.attempts.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-500">{t('No attempts yet')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedQuizHistory.attempts.map((attempt, index) => {
                    const score = parseFloat(attempt.score) || 0;
                    const isPassed = attempt.passed;
                    const isBestScore = score === Math.max(...selectedQuizHistory.attempts.map(a => parseFloat(a.score) || 0));

                    return (
                      <div
                        key={attempt.id}
                        className={`p-4 border-2 rounded-lg ${
                          isBestScore ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-gray-700">
                                {t('Attempt #{{number}}', { number: selectedQuizHistory.attempts.length - index })}
                              </span>
                              {isBestScore && (
                                <span className="px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                                  {t('Best')}
                                </span>
                              )}
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  isPassed
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {isPassed ? t('Passed') : t('Failed')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {formatDateTime(attempt.started_at)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold text-gray-800">
                              {score.toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {t('{{earned}} / {{total}} pts', { earned: attempt.points_earned || 0, total: attempt.total_points || 0 })}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                {t('Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LessonDetail;
